import { defineStore } from "pinia";
import { ref } from "vue";
import { watchDebounced } from "@vueuse/core";
import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";

export const useSqlQueryStore = defineStore("sqlQuery", () => {
  const templates = ref([]);
  const recentRuns = ref([]);
  const templatesSearchQuery = ref("");

  async function fetchTemplates() {
    const params = new URLSearchParams();
    if (templatesSearchQuery.value) params.append("name", templatesSearchQuery.value);

    const { response, data } = await useMyFetch(`/sql_query_templates?${params}`).json().get();
    if (response.value.ok) {
      templates.value = data.value.templates;
    }
  }

  async function fetchRecentRuns() {
    const { response, data } = await useMyFetch(
      "/jobs?job_type=query,library_query&mine_only=true&per_page=10&page=1",
    )
      .json()
      .get();
    if (response.value.ok) {
      recentRuns.value = data.value.jobs;
    }
  }

  async function createAndRunJob(jobPayload) {
    const { response, data } = await useMyFetch("/jobs").json().post(jobPayload);
    if (!response.value.ok) throw new Error("Failed to create job");

    const job = data.value;

    const { response: runResponse } = await useMyFetch(`/jobs/${job.id}/run`).json().get();
    if (!runResponse.value.ok) throw new Error("Failed to run job");

    captureEvent("sql_query_run", { job_type: jobPayload.job_type });

    await fetchRecentRuns();
    return job;
  }

  async function runCustomQuery({ name, spreadsheet_id, tab_name, query }) {
    return createAndRunJob({
      name: name || "Custom Query",
      job_type: "query",
      query,
      job_attributes: { spreadsheet_id, tab_name },
    });
  }

  async function runLibraryQuery({ name, template_id, spreadsheet_id, tab_name, template_params }) {
    return createAndRunJob({
      name: name || "Library Query",
      job_type: "library_query",
      job_attributes: {
        template_id,
        spreadsheet_id,
        tab_name,
        template_params: template_params || {},
      },
    });
  }

  async function rerunJob(jobId) {
    const { response } = await useMyFetch(`/jobs/${jobId}/run`).json().get();
    if (!response.value.ok) throw new Error("Failed to re-run job");

    captureEvent("sql_query_rerun", { job_id: jobId });

    await fetchRecentRuns();
  }

  async function deleteRun(jobId) {
    const { response } = await useMyFetch(`/jobs/${jobId}`).json().delete();
    if (response.value.ok) {
      recentRuns.value = recentRuns.value.filter((j) => j.id !== jobId);
    }
  }

  async function createTemplate(payload) {
    const { response, data } = await useMyFetch("/sql_query_templates")
      .json()
      .post({ sql_query_template: payload });
    if (!response.value.ok) throw new Error("Failed to create template");

    captureEvent("sql_query_template_created");

    await fetchTemplates();
    return data.value;
  }

  async function updateTemplate(id, payload) {
    const { response, data } = await useMyFetch(`/sql_query_templates/${id}`)
      .json()
      .put({ sql_query_template: payload });
    if (!response.value.ok) throw new Error("Failed to update template");

    captureEvent("sql_query_template_updated", { template_id: id });

    await fetchTemplates();
    return data.value;
  }

  async function deleteTemplate(id) {
    const { response } = await useMyFetch(`/sql_query_templates/${id}`).json().delete();
    if (!response.value.ok) throw new Error("Failed to delete template");

    captureEvent("sql_query_template_deleted", { template_id: id });

    templates.value = templates.value.filter((t) => t.id !== id);
  }

  function updateJobStatus(jid, status) {
    const job = recentRuns.value.find((j) => j.jid === jid);
    if (job) job.status = status;
  }

  watchDebounced(templatesSearchQuery, () => fetchTemplates(), { debounce: 300 });

  return {
    templates,
    recentRuns,
    templatesSearchQuery,
    fetchTemplates,
    fetchRecentRuns,
    runCustomQuery,
    runLibraryQuery,
    rerunJob,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    deleteRun,
    updateJobStatus,
  };
});
